import { setActivityDrawer } from '@store/appStore';
import { buildCatalogOrigin } from '@api/ActivityLogs/CatalogActivity';
import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, InputAdornment, Stack, TextField, Typography, Menu, MenuItem, IconButton } from '@mui/material';
import useOpenModal from '@hooks/useOpenModal';
import useConfirmationModal from '@hooks/useConfirmationModal';
import { hideLoader, showLoader } from '@utils/Loader';
import { showAlertError } from '@utils/Alerts/Error';
import { showAlertMessage } from '@utils/Alerts/Message';
import { ModalSaveInput } from '@screens/Cedis/components/ModalAddInput';
import InputsService from '@api/Inputs/InputCtrl';
import { formatUnitCost, getUnitCost, Input } from '@api/Inputs/Input';
import { useDispatch, useSelector } from 'react-redux';
import { RootReducer } from '@store/index';
import { ProfileTypes } from '@api/Users/User';
import Grid from '@mui/material/Grid';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SettingsIcon from '@mui/icons-material/Settings';
import { ResponsiveDataGrid, ResponsiveGridColDef } from '@components/Utilities/Tables/ResponsiveDataGrid';
import { ModalConfirmation } from '@components/Utilities/Modals/ModalConfirmation';

const ListInputs: React.FunctionComponent = () => {
    const { profileSelected, distributionCenterSelected } = useSelector((state: RootReducer) => state.auth);
    const inputsService = useMemo(() => new InputsService(), []);
    const [inputs, setInputs] = useState<Input[]>([]);
    const [currentInput, setCurrentInput] = useState<Input | undefined>(undefined);
    const [filters, setFilters] = useState({
        search: ''
    });
    const modalSaveInput = useOpenModal();
    const modalConfirmation = useConfirmationModal();

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedInput, setSelectedInput] = useState<Input | undefined>(undefined);

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, row: Input) => {
        setAnchorEl(event.currentTarget);
        setSelectedInput(row);
    };

    const dispatch = useDispatch();

    /**
     * Abre la bitácora de este objeto (M2). Antes de esto, el catálogo escribía
     * renglones que **nadie podía ver**: no había puerta en ninguna de sus
     * pantallas.
     *
     * El snapshot se toma ANTES de cerrar el menú, que es donde se pierde la
     * selección, y el renglón de creación sale del propio documento (D8): no
     * cuesta una lectura.
     */
    const handleActivity = () => {
        const item = selectedInput;
        handleMenuClose();
        if (!item?.id) return;
        dispatch(
            setActivityDrawer({
                isOpen: true,
                section: 'input',
                sectionId: item.id,
                derived: buildCatalogOrigin(item, 'creó el insumo', 'Insumo creado')
            })
        );
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleEdit = () => {
        handleMenuClose();
        if (selectedInput) {
            setCurrentInput(selectedInput);
            modalSaveInput.toggle();
        }
    };

    const handleDeleteClick = () => {
        handleMenuClose();
        if (selectedInput) {
            modalConfirmation.setModalData(
                'Eliminar insumo',
                `¿Estás seguro de que deseas eliminar el insumo "${selectedInput.name}"? Esta acción no se puede deshacer.`
            );
        }
    };

    const handleDeleteConfirm = async () => {
        if (selectedInput?.id) {
            showLoader();
            try {
                await inputsService.deleteInput(selectedInput.id);
                showAlertMessage('Insumo eliminado exitosamente');
                await getData();
            } catch (error) {
                showAlertError(error);
            }
            hideLoader();
        }
    };

    const getData = async () => {
        showLoader();
        try {
            if (profileSelected === ProfileTypes.DistributionCenter && distributionCenterSelected?.id) {
                const data = await inputsService.getInputs();
                setInputs(data);
            } else {
                const data = await inputsService.getInputs();
                setInputs(data);
            }
        } catch (error: any) {
            showAlertError(error);
        }
        hideLoader();
    };

    const onOpenModalAddInput = () => {
        setCurrentInput(undefined);
        modalSaveInput.toggle();
    };

    const onSaveInput = async (input: Input) => {
        showLoader();
        try {
            const newData = await inputsService.saveInput(input);
            setCurrentInput(newData);
            showAlertMessage('Insumo guardado exitosamente');
            await getData();
            modalSaveInput.toggle();
        } catch (error) {
            showAlertError(error);
        }
        hideLoader();
    };

    useEffect(() => {
        void getData();
    }, []);

    const columns = useMemo<ResponsiveGridColDef<Input>[]>(
        () => [
            {
                field: 'category',
                flex: 1,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Categoria
                    </Typography>
                ),
                renderCell: ({ row }) => <Typography>{row.inputCategoryData?.name || 'Sin categoria'}</Typography>
            },
            {
                field: 'name',
                flex: 1,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Insumo
                    </Typography>
                ),
                renderCell: ({ row }) => <Typography>{row.name}</Typography>
            },
            {
                field: 'presentation',
                flex: 1,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Presentación
                    </Typography>
                ),
                renderCell: ({ row }) => <Typography>{`${row.presentation.value} ${row.presentation.unit}`}</Typography>
            },
            {
                field: 'unit',
                flex: 1,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Unidad
                    </Typography>
                ),
                renderCell: ({ row }) => <Typography>{`${row.unit.value} ${row.unit.unit}`}</Typography>
            },
            {
                field: 'purchaseCost',
                flex: 1,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Compra
                    </Typography>
                ),
                renderCell: ({ row }) => <Typography>{`$${row.purchaseCost}`}</Typography>
            },
            {
                field: 'costOfSale',
                flex: 1,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Venta
                    </Typography>
                ),
                renderCell: ({ row }) => <Typography>{`$${row.costOfSale}`}</Typography>
            },
            {
                field: 'unitCost',
                flex: 1,
                showInMobile: false,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Costo unitario
                    </Typography>
                ),
                renderCell: ({ row }) => (
                    <Typography>{`${formatUnitCost(getUnitCost(row))} / ${row.unit.unit}`}</Typography>
                )
            },
            {
                field: 'actions',
                flex: 1 / 2,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                headerAlign: 'center',
                align: 'center',
                renderHeader: () => <SettingsIcon sx={{ color: '#809FB8' }} />,
                renderCell: ({ row }) => (
                    <IconButton onClick={(e) => handleMenuOpen(e, row)}>
                        <MoreVertIcon />
                    </IconButton>
                )
            }
        ],
        []
    );

    return (
        <Stack height="100%" spacing={2}>
            <Box>
                <Grid container spacing={2}>
                    <Grid item xs={6} />
                    <Grid item xs={12} md={3} textAlign="right">
                        <Button variant="contained" color="primary" onClick={onOpenModalAddInput} endIcon={<AddIcon />}>
                            Nuevo insumo
                        </Button>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Buscar insumo"
                            value={filters.search}
                            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <SearchIcon />
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Grid>
                </Grid>
            </Box>
            <ResponsiveDataGrid
                searchValue={filters.search}
                rows={inputs}
                columns={columns}
                onCellDoubleClick={(cel) => {
                    setCurrentInput(cel.row as Input);
                    modalSaveInput.toggle();
                }}
            />
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                <MenuItem onClick={handleEdit}>Editar</MenuItem>
                <MenuItem onClick={handleActivity}>Actividad</MenuItem>
                <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
                    Eliminar
                </MenuItem>
            </Menu>
            <ModalSaveInput modalSaveInput={modalSaveInput} input={currentInput} onSaveInput={onSaveInput} />
            <ModalConfirmation modalConfirmation={modalConfirmation} onConfirmation={handleDeleteConfirm} />
        </Stack>
    );
};
export default ListInputs;
